import {
  IAIVideoGenerationRequestBody,
  IAIVideoModel,
  IAIVideoGenerationParams,
  IAIVideoProjectMeta,
  IApiReferenceMedias,
  IReferenceMediaDimensions,
  IReferenceMediaItem,
  IReferenceMedias,
  ReferenceMediaImageType,
  TFirstAndLastFrameRule,
  TReferenceMode
} from 'types/aiVideo'
import { IAIVideoState } from 'store/types/aiVideo'
import {
  PROJECT_MEDIA_TYPES,
  PROJECT_MAX_DURATION,
  ASPECT_RATIO_VALUES
} from 'consts'
import { IDimensions } from 'types'
import { IMediaItem, MediaStatus } from 'types/media'
import { calculateMediaRect } from 'utils/media'
import { FIT } from 'consts/canvas'
import { getMediaParameters } from 'hooks/project-item-upload/use-project-media-upload/helpers'
import { removeUrlQueryParams } from 'utils'
import { IUnlimitedAiVideoModel } from 'types/user'
import { MIN_PREMIUM_QUALITY } from 'consts'
import { ReferenceMode } from 'containers/SubSidebar/AIVideoContent/consts'

export const getBestFreeQualityOption = <T extends { quality: number }>(
  qualities: T[]
): T | null => {
  const freeQualities = qualities.filter(q => q.quality < MIN_PREMIUM_QUALITY)
  if (!freeQualities.length) return null
  return freeQualities.reduce((max, q) => (q.quality > max.quality ? q : max))
}

export const evaluateFirstAndLastFrameRule = (
  rule: TFirstAndLastFrameRule,
  params: { duration: number; quality: number; aspectRatio: string | null }
): boolean => {
  if ('match' in rule) {
    return rule.match === 'all'
      ? rule.rules.every(r => evaluateFirstAndLastFrameRule(r, params))
      : rule.rules.some(r => evaluateFirstAndLastFrameRule(r, params))
  }

  const fieldValues = {
    videoDurationSeconds: params.duration,
    quality: params.quality,
    aspectRatio: params.aspectRatio ?? undefined
  }

  return fieldValues[rule.field] === rule.value
}

export const findMatchingAspectRatio = (
  imageDimensions: IReferenceMediaDimensions
): string | undefined => {
  const imageRatio = imageDimensions.width / imageDimensions.height
  return Object.keys(ASPECT_RATIO_VALUES).find(
    ratio => imageRatio.toFixed(1) === ASPECT_RATIO_VALUES[ratio].toFixed(1)
  )
}

export const createReferenceMediaItem = (
  type: ReferenceMediaImageType,
  url: string,
  isLoading = false,
  dimensions?: IReferenceMediaDimensions
): IReferenceMediaItem => ({
  id: crypto.randomUUID(),
  type,
  url,
  isLoading,
  ...(dimensions
    ? {
        dimensions,
        nativeRatio: findMatchingAspectRatio(dimensions)
      }
    : {})
})

const fetchImageDimensionsAndNativeRatioFromUrl = async (
  url: string
): Promise<
  | {
      dimensions: IReferenceMediaDimensions
      nativeRatio?: string
    }
  | undefined
> => {
  const dimensions = await new Promise<IReferenceMediaDimensions | undefined>(
    resolve => {
      if (!url?.trim()) {
        resolve(undefined)
        return
      }
      const img = new Image()
      img.onload = () => {
        const w = img.naturalWidth
        const h = img.naturalHeight
        if (w > 0 && h > 0) resolve({ width: w, height: h })
        else resolve(undefined)
      }
      img.onerror = () => resolve(undefined)
      img.src = url
    }
  )
  if (!dimensions) return undefined
  const nativeRatio = findMatchingAspectRatio(dimensions)
  return {
    dimensions,
    ...(nativeRatio !== undefined ? { nativeRatio } : {})
  }
}

export const buildReferenceMediaFrameWhenMissingDimensions = async (
  frame: IReferenceMediaItem
): Promise<IReferenceMediaItem> => {
  if (frame.nativeRatio) return frame
  const resolved = await fetchImageDimensionsAndNativeRatioFromUrl(frame.url)
  if (!resolved) return frame
  return {
    ...frame,
    dimensions: resolved.dimensions,
    ...(resolved.nativeRatio ? { nativeRatio: resolved.nativeRatio } : {})
  }
}

const buildFramesBucket = (
  firstFrameUrl?: string | null,
  lastFrameUrl?: string | null,
  firstFrameDimensions?: IReferenceMediaDimensions,
  lastFrameDimensions?: IReferenceMediaDimensions
): IReferenceMediaItem[] => {
  const items: IReferenceMediaItem[] = []
  if (firstFrameUrl) {
    items.push(
      createReferenceMediaItem(
        ReferenceMediaImageType.FIRST_FRAME,
        firstFrameUrl,
        false,
        firstFrameDimensions
      )
    )
  }
  if (lastFrameUrl) {
    items.push(
      createReferenceMediaItem(
        ReferenceMediaImageType.LAST_FRAME,
        lastFrameUrl,
        false,
        lastFrameDimensions
      )
    )
  }
  return items
}

export const swapFramesBucket = (
  frames: IReferenceMediaItem[]
): IReferenceMediaItem[] => {
  return frames.map(frame => {
    if (frame.type === ReferenceMediaImageType.FIRST_FRAME) {
      return { ...frame, type: ReferenceMediaImageType.LAST_FRAME }
    }
    if (frame.type === ReferenceMediaImageType.LAST_FRAME) {
      return { ...frame, type: ReferenceMediaImageType.FIRST_FRAME }
    }
    return frame
  })
}

export const buildReferenceFrames = (
  firstFrameUrl?: string | null,
  lastFrameUrl?: string | null,
  firstFrameDimensions?: IReferenceMediaDimensions,
  lastFrameDimensions?: IReferenceMediaDimensions
): IReferenceMedias | undefined => {
  if (!firstFrameUrl && !lastFrameUrl) return
  return {
    frames: buildFramesBucket(
      firstFrameUrl,
      lastFrameUrl,
      firstFrameDimensions,
      lastFrameDimensions
    )
  }
}

const getFrameImage = (
  referenceMedias: IReferenceMedias | undefined,
  type: ReferenceMediaImageType
) => referenceMedias?.frames?.find(img => img.type === type)

const getFrameUrl = (
  referenceMedias: IReferenceMedias | undefined,
  type: ReferenceMediaImageType
): string | null => getFrameImage(referenceMedias, type)?.url ?? null

export const getFirstFrameUrl = (referenceMedias?: IReferenceMedias) =>
  getFrameUrl(referenceMedias, ReferenceMediaImageType.FIRST_FRAME)

export const getLastFrameUrl = (referenceMedias?: IReferenceMedias) =>
  getFrameUrl(referenceMedias, ReferenceMediaImageType.LAST_FRAME)

export const getFirstFrameDimensions = (referenceMedias?: IReferenceMedias) =>
  getFrameImage(referenceMedias, ReferenceMediaImageType.FIRST_FRAME)
    ?.dimensions ?? null

export const getLastFrameDimensions = (referenceMedias?: IReferenceMedias) =>
  getFrameImage(referenceMedias, ReferenceMediaImageType.LAST_FRAME)
    ?.dimensions ?? null

export const getFirstFrameNativeRatio = (referenceMedias?: IReferenceMedias) =>
  getFrameImage(referenceMedias, ReferenceMediaImageType.FIRST_FRAME)
    ?.nativeRatio ?? null

export const getLastFrameNativeRatio = (referenceMedias?: IReferenceMedias) =>
  getFrameImage(referenceMedias, ReferenceMediaImageType.LAST_FRAME)
    ?.nativeRatio ?? null

// first frame or the first ref image, if it is multiRef request
export const getFirstFrameOrRefImageUrlFromApi = (
  referenceMedias?: IApiReferenceMedias
): string | null =>
  referenceMedias?.images?.find(
    img => img.type === ReferenceMediaImageType.FIRST_FRAME
  )?.url ??
  referenceMedias?.images?.[0]?.url ??
  null

export const getActiveReferenceItems = (
  referenceMedias: IReferenceMedias | undefined,
  mode: TReferenceMode
): IReferenceMediaItem[] =>
  (mode === ReferenceMode.IMAGES
    ? referenceMedias?.images
    : referenceMedias?.frames) ?? []

export const toApiReferenceMedias = (
  items?: IReferenceMediaItem[]
): IApiReferenceMedias | undefined => {
  if (!items?.length) return undefined
  return {
    images: items.map(({ type, url }) => ({ type, url }))
  }
}

// by default if model has multiRef support, show multiRef,
// but if user already uploaded frames in another model, show frames as default tab
export const getDefaultReferenceMode = (
  model?: IAIVideoModel,
  hasUploadedOnlyFirstFrame: boolean = false
): TReferenceMode =>
  model?.capabilities.multipleReferenceMedias?.isSupported &&
  !hasUploadedOnlyFirstFrame
    ? ReferenceMode.IMAGES
    : ReferenceMode.FRAMES

export const apiToStateReferenceMedias = (
  apiMedias?: IApiReferenceMedias,
  firstFrameDimensions?: IReferenceMediaDimensions
): IReferenceMedias | undefined => {
  if (!apiMedias?.images?.length) return undefined
  const frames = apiMedias.images
    .filter(
      img =>
        img.type === ReferenceMediaImageType.FIRST_FRAME ||
        img.type === ReferenceMediaImageType.LAST_FRAME
    )
    .map(img => {
      const dims =
        img.dimensions ??
        (img.type === ReferenceMediaImageType.FIRST_FRAME
          ? firstFrameDimensions
          : undefined)
      const item = createReferenceMediaItem(img.type, img.url, false, dims)
      if (!item.nativeRatio && img.nativeRatio) {
        return { ...item, nativeRatio: img.nativeRatio }
      }
      return item
    })

  const images = apiMedias.images
    .filter(img => img.type === ReferenceMediaImageType.REFERENCE_IMAGE)
    .map(img => createReferenceMediaItem(img.type, img.url))

  if (!frames.length && !images.length) return undefined

  return {
    ...(frames.length ? { frames } : {}),
    ...(images.length ? { images } : {})
  }
}

interface IBuildAIVideoMediaItemParams {
  referenceImageUrl?: string | null
  defaultDimensions: IDimensions
  videoDurationSeconds: number
  insertionStartTime: number
  zIndex: number
  id: string
  assetId: string
  width: number
  height: number
}
/**
 * Helper function to check if the AI video generation form has changes
 * @param current - The current generation parameters
 * @param original - The original generation parameters
 * @returns True if the form has changes, false otherwise
 */
const getImagesBucketUrls = (medias?: IReferenceMedias): string =>
  (medias?.images ?? []).map(i => i.url).join(',')

export const hasFormChanges = (
  current: IAIVideoGenerationParams,
  original: IAIVideoGenerationParams
): boolean => {
  return (
    current.prompt !== original.prompt ||
    getFirstFrameUrl(current.referenceMedias) !==
      getFirstFrameUrl(original.referenceMedias) ||
    getLastFrameUrl(current.referenceMedias) !==
      getLastFrameUrl(original.referenceMedias) ||
    getImagesBucketUrls(current.referenceMedias) !==
      getImagesBucketUrls(original.referenceMedias)
  )
}

const getFramesMatchingNativeRatio = (
  referenceMedias: IReferenceMedias | undefined,
  availableRatios: string[]
): string | undefined => {
  const firstNative = getFirstFrameNativeRatio(referenceMedias)
  if (!firstNative || !availableRatios.includes(firstNative)) return undefined

  const lastFrameUrl = getLastFrameUrl(referenceMedias)
  if (!lastFrameUrl) return firstNative

  const lastNative = getLastFrameNativeRatio(referenceMedias)
  return lastNative === firstNative ? firstNative : undefined
}

export function getDefaultImageAspectRatio(
  state: IAIVideoState
): string | null | undefined {
  const firstFrameUrl = getFirstFrameUrl(state.generationParams.referenceMedias)
  if (!firstFrameUrl) return undefined

  const model = state.aiVideoModels.find(
    m => m.model === state.generationParams.selectedModel
  )
  const nativeRatio = getFramesMatchingNativeRatio(
    state.generationParams.referenceMedias,
    model?.capabilities.aspectRatios ?? []
  )

  if (nativeRatio) return nativeRatio
  if (model?.capabilities.isMediaResizingSupported) return undefined
  return null
}

interface IBuildAIVideoGenerationRequestParams {
  id?: number
  referenceMedias?: IApiReferenceMedias
  aiImageId: number | null
  prompt: string
  quality: number
  videoDurationSeconds: number
  videoModel: string
  aspectRatio: string | undefined
  projectId?: number
  styleId?: number | null
  timelineItemId?: string | null
  parentProjectId?: number
  isFastTrack?: boolean
}

export const buildAIVideoGenerationRequest = (
  params: IBuildAIVideoGenerationRequestParams
): IAIVideoGenerationRequestBody => {
  // Remove query parameters from image URLs if present
  // TODO: remove after image cross origin is fixed
  const referenceMedias = params.referenceMedias
    ? {
        ...params.referenceMedias,
        images: params.referenceMedias.images?.map(
          ({ dimensions: _, nativeRatio: __, ...image }) => ({
            ...image,
            url: removeUrlQueryParams(image.url) || image.url
          })
        )
      }
    : undefined

  const baseRequest = {
    id: params.id,
    referenceMedias,
    prompt: params.prompt,
    videoDurationSeconds: params.videoDurationSeconds,
    videoModel: params.videoModel,
    ...(params.aspectRatio ? { aspectRatio: params.aspectRatio } : {}),
    quality: params.quality,
    styleId: params.styleId || undefined,
    ...(params.projectId ? { videoProjectId: params.projectId } : {}),
    // Only emit fastTrack when on. Backend treats absence as standard.
    ...(params.isFastTrack ? { fastTrack: true } : {})
  }

  const meta: IAIVideoProjectMeta = {}

  if (params.projectId) {
    meta.sourceProjectId = params.projectId
  }

  if (params.parentProjectId) {
    meta.parentProjectId = params.parentProjectId
  }

  if (params.aiImageId) {
    meta.reference = {
      id: params.aiImageId,
      type: PROJECT_MEDIA_TYPES.aiImage
    }
  }

  // Only include meta if it has any properties
  if (Object.keys(meta).length > 0) {
    return {
      ...baseRequest,
      meta
    }
  }

  return baseRequest
}

/*
 * Builds a media item for AI video generation based on the input parameters.
 */
export const buildAIVideoMediaItem = async ({
  referenceImageUrl,
  defaultDimensions,
  videoDurationSeconds = 5,
  insertionStartTime,
  zIndex,
  id,
  assetId,
  width,
  height
}: IBuildAIVideoMediaItemParams) => {
  const { width: finalWidth, height: finalHeight } = referenceImageUrl
    ? await getMediaParameters(referenceImageUrl, 'image')
    : { width, height }

  const mediaRect = calculateMediaRect({
    mediaDimensions: { width: finalWidth, height: finalHeight },
    mode: FIT,
    canvasDimensions: defaultDimensions,
    rotation: 0
  })

  return {
    id,
    assetId,
    type: PROJECT_MEDIA_TYPES.aiVideo,
    height: mediaRect.height,
    width: mediaRect.width,
    rotation: 0,
    duration: videoDurationSeconds,
    startTime: insertionStartTime,
    zIndex,
    coordinates: {
      x: mediaRect.x,
      y: mediaRect.y
    },
    meta: {
      path: referenceImageUrl || null,
      originalHeight: finalHeight,
      originalWidth: finalWidth,
      originalDuration: videoDurationSeconds,
      trimStart: 0,
      status: MediaStatus.PENDING,
      volume: 100
    }
  }
}

interface ICheckAndShiftMediaItemsParams {
  mediaItems: IMediaItem[]
  selectedItemIndex: number
  newItemEndTime: number
}

interface ICheckAndShiftMediaItemsResult {
  shouldShift: boolean
  updatedMediaItems?: IMediaItem[]
  shiftAmount?: number
  error?: boolean
}

/**
 * Checks if there's enough space for a regenerated AI video and shifts items if needed
 * @param mediaItems - Array of all media items in the project
 * @param selectedItemIndex - Index of the selected AI video item
 * @param newItemEndTime - The new end time of the regenerated video
 * @returns Object containing whether shifting is needed and updated items if so
 */
export const checkAndShiftMediaItems = ({
  mediaItems,
  selectedItemIndex,
  newItemEndTime
}: ICheckAndShiftMediaItemsParams): ICheckAndShiftMediaItemsResult => {
  // Check if the new duration would exceed project maximum duration
  if (newItemEndTime > PROJECT_MAX_DURATION) {
    return {
      shouldShift: false,
      error: true
    }
  }

  const nextItem = mediaItems[selectedItemIndex + 1]

  if (nextItem) {
    const spaceToAdd = nextItem.startTime - newItemEndTime

    // If there's not enough space, shift all subsequent items to the right
    if (spaceToAdd < 0) {
      const shiftAmount = Math.abs(spaceToAdd)

      const updatedMediaItems = mediaItems.map((item, index) => {
        // Shift all items that come after the selected item
        if (index > selectedItemIndex) {
          return {
            ...item,
            startTime: item.startTime + shiftAmount
          }
        }
        return item
      })

      return {
        shouldShift: true,
        updatedMediaItems,
        shiftAmount
      }
    }
  }

  return {
    shouldShift: false
  }
}

export const updateAiImageMediaItemToVideoGeneration = (
  mediaItem: IMediaItem,
  aiVideoProjectHash: string,
  defaultDimensions: IDimensions,
  videoDurationSeconds: number,
  isAudioSupported: boolean
) => {
  return {
    ...mediaItem,
    type: PROJECT_MEDIA_TYPES.aiVideo,
    assetId: aiVideoProjectHash,
    width: mediaItem.width || defaultDimensions.width,
    height: mediaItem.height || defaultDimensions.height,
    meta: {
      ...mediaItem.meta,
      trimStart: 0,
      originalDuration: videoDurationSeconds,
      status: MediaStatus.PENDING,
      path: mediaItem.meta.path || null,
      assetParentId: undefined,
      ...(isAudioSupported ? { volume: 100 } : {})
    }
  }
}

export const getDefaultAIVideoModel = (aiVideoModels: IAIVideoModel[]) => {
  return (
    aiVideoModels.find(model => model.isDefaultFor.includes('v-editor')) ||
    aiVideoModels[0]
  )
}

export const getAvailableDuration = ({
  itemStartTime,
  newDuration
}: {
  itemStartTime: number
  newDuration: number
}) => {
  const durationRelatedToProjectMaxDuration = Math.min(
    itemStartTime + newDuration,
    PROJECT_MAX_DURATION
  )

  const duration = Math.min(durationRelatedToProjectMaxDuration, newDuration)

  return duration
}

export const getDefaultAIVideoModelGenerationParams = (
  defaultModel?: IAIVideoModel
): IAIVideoGenerationParams => {
  // Preferences are applied per-user at runtime (`getAIVideoModels` / on generate);
  // this default builder runs without a userId, so it uses model defaults only.
  const quality = defaultModel?.defaultValues.quality || 0
  const isFastTrack = false

  return {
    prompt: '',
    duration: defaultModel?.defaultValues.duration || 0,
    selectedModel: defaultModel?.model || null,
    aspectRatio: defaultModel?.capabilities.aspectRatios?.[0] || null,
    quality,
    referenceMedias: undefined,
    targetMediaItemId: null,
    aiImageId: null,
    isConsistencyEnabled: true,
    smartPrompt: '',
    isFastTrack,
    refImageSelectedStyleId: undefined
  }
}

const _getClosestPossibleValue = (value: number, items: Array<number>) => {
  return items.reduce((closest, current) => {
    return Math.abs(current - value) < Math.abs(closest - value)
      ? current
      : closest
  }, items[0])
}
export const findMatchingOption = <T, K extends keyof T>({
  items,
  key,
  selectedValue,
  defaultValue
}: {
  items: T[]
  key: K
  selectedValue?: T[K]
  defaultValue?: T[K]
}): T => {
  const currentKeyValues = items.map(item => item[key]) as Array<number>
  const closestPossibleValue =
    typeof selectedValue === 'number'
      ? _getClosestPossibleValue(selectedValue, currentKeyValues)
      : selectedValue
  return (
    items.find(item => item[key] === closestPossibleValue) ??
    items.find(item => item[key] === defaultValue) ??
    items[0]
  )
}

export const getModelDefaultOptionCredits = ({
  selectedModel,
  unlimitedVideoModelsData,
  selectedQuality
}: {
  selectedModel: IAIVideoModel
  unlimitedVideoModelsData?: IUnlimitedAiVideoModel[]
  selectedQuality?: number
}) => {
  const { duration, quality } = selectedModel.defaultValues

  const _quality = selectedQuality ?? quality
  const unlimitedModel = unlimitedVideoModelsData?.find(
    unlimitedModel => unlimitedModel.aiVideoModelId === selectedModel.id
  )
  if (
    unlimitedModel &&
    unlimitedModel.qualities.some(item => item.value === _quality)
  ) {
    return 0
  }

  const defaultOption = selectedModel.options.find(
    option => option.duration === duration
  )

  const matchingQualityOption = defaultOption?.qualities.find(
    option => option.quality === _quality
  )
  return matchingQualityOption?.credit || 0
}

export const getStringifiedQuality = (quality: number) => {
  const qualityMap: Record<number, string> = {
    2160: '2160p 4K',
    1080: '1080p Full HD',
    768: '720p HD', // business request
    720: '720p HD',
    540: '540p',
    480: '480p',
    360: '360p'
  }

  return qualityMap[quality] || `${quality}p`
}
